import axios from 'axios';
// eslint-disable-next-line import/no-cycle
import { filesApi } from '@/includes/filesApi';

// Create axios instance for notification service
const notificationApi = axios.create({
  baseURL: '/notification-api', // Use relative path instead of environment variable
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: set the token dynamically
// Note: Using localStorage directly to avoid circular dependency with store
notificationApi.interceptors.request.use(
  (config) => {
    // Get token from localStorage (store also uses this as fallback)
    const token = localStorage.getItem('jwt') || localStorage.getItem('token');

    // Create a new config object to avoid directly mutating the parameter
    const newConfig = { ...config, headers: { ...config.headers } };

    if (token) {
      // Check if token already has Bearer prefix
      const tokenValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      newConfig.headers.Authorization = tokenValue;
    }
    return newConfig;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: handle 401 errors
notificationApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Lazy import router to avoid circular dependency
      const { default: router } = await import('@/router');
      if (router.currentRoute.value.name !== 'login') {
        router.push('/auth');
      }
    }
    return Promise.reject(error);
  },
);

export default {
  /**
   * List templates with filters
   * @param {Object} filters - Filter options
   * @param {string} filters.notificationType - Filter by notification type
   * @param {string} filters.channel - Filter by channel (email, sms, push, popup, overlay)
   * @param {string} filters.language - Filter by language (en, de)
   * @param {boolean} filters.isActive - Filter by active status
   * @param {number} filters.page - Page number (default: 1)
   * @param {number} filters.limit - Items per page (default: 20)
   * @returns {Promise<{templates: Array, total: number, page: number, limit: number}>}
   */
  async listTemplates(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.notificationType) params.append('notificationType', filters.notificationType);
      if (filters.channel) params.append('channel', filters.channel);
      if (filters.language) params.append('language', filters.language);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      // Use relative path - axios will combine it with baseURL
      // baseURL is '/notification-api', so 'templates' becomes '/notification-api/templates'
      const urlPath = params.toString() ? `templates?${params.toString()}` : 'templates';

      const response = await notificationApi.get(urlPath);

      // NestJS returns the service result directly:
      // { templates: [], total: number, page: number, limit: number }
      // response.data IS the object { templates, total, page, limit },
      // NOT { data: { templates, ... } }
      const {
        templates,
        total,
        page,
        limit,
      } = response.data || {};

      // The backend service returns: { templates, total, page, limit }
      if (templates && Array.isArray(templates)) {
        return {
          templates,
          total: total || 0,
          page: page || filters.page || 1,
          limit: limit || filters.limit || 20,
        };
      }

      // Fallback: return empty if structure is unexpected
      return {
        templates: [],
        total: 0,
        page: filters.page || 1,
        limit: filters.limit || 20,
      };
    } catch (error) {
      console.error('Failed to list templates:', error);
      throw error;
    }
  },

  /**
   * Get a single template by ID
   * @param {string} id - Template ID
   * @returns {Promise<Object>} Template object
   */
  async getTemplate(id) {
    try {
      const response = await notificationApi.get(`/templates/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to get template:', error);
      if (error.response?.status === 404) {
        throw new Error('Template not found');
      }
      throw error;
    }
  },

  /**
   * Create a new template
   * @param {Object} templateData - Template data
   * @param {string} templateData.notificationType - Notification type
   * @param {string} templateData.channel - Channel (email, sms, push, popup, overlay)
   * @param {string} templateData.language - Language (en, de)
   * @param {string} templateData.fileId - File ID (for email templates)
   * @param {string|Object} templateData.content - Content (for non-email templates)
   * @param {string} templateData.createdBy - Creator user ID
   * @returns {Promise<Object>} Created template
   */
  async createTemplate(templateData) {
    try {
      const response = await notificationApi.post('/templates', templateData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to create template:', error);
      if (error.response?.status === 422) {
        throw new Error(error.response.data?.message || 'Validation error');
      }
      throw error;
    }
  },

  /**
   * Update an existing template
   * @param {string} id - Template ID
   * @param {Object} templateData - Updated template data
   * @param {string} templateData.updatedBy - Updater user ID
   * @returns {Promise<Object>} Updated template
   */
  async updateTemplate(id, templateData) {
    try {
      const response = await notificationApi.put(`/templates/${id}`, templateData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to update template:', error);
      if (error.response?.status === 404) {
        throw new Error('Template not found');
      }
      if (error.response?.status === 422) {
        throw new Error(error.response.data?.message || 'Validation error');
      }
      throw error;
    }
  },

  /**
   * Delete a template
   * @param {string} id - Template ID
   * @param {boolean} hardDelete - Whether to hard delete (default: false)
   * @returns {Promise<void>}
   */
  async deleteTemplate(id, hardDelete = false) {
    try {
      const params = hardDelete ? '?hardDelete=true' : '';
      await notificationApi.delete(`/templates/${id}${params}`);
    } catch (error) {
      console.error('Failed to delete template:', error);
      if (error.response?.status === 404) {
        throw new Error('Template not found');
      }
      throw error;
    }
  },

  /**
   * Upload email template file directly to files service
   * @param {File|Blob} file - File to upload
   * @returns {Promise<string>} File ID
   */
  async uploadEmailTemplateFile(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Upload directly to files service
      // Don't set Content-Type header - let browser/axios set it automatically with boundary
      const response = await filesApi.post('/files/upload', formData);

      return response.data.data?.fileId || response.data.fileId;
    } catch (error) {
      console.error('Failed to upload email template file:', error);
      throw error;
    }
  },

  /**
   * Get template file content from files service
   * @param {string} fileId - File ID
   * @returns {Promise<string>} File content
   */
  async getTemplateFileContent(fileId) {
    try {
      const response = await filesApi.get(`/files/download/${fileId}`, {
        responseType: 'text',
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch template file:', error);
      if (error.response?.status === 404) {
        throw new Error('Template file not found');
      }
      throw error;
    }
  },

  /**
   * Validate that a template exists and is active
   * @param {string} templateId - Template ID
   * @param {string} channel - Channel to validate
   * @returns {Promise<{exists: boolean, isActive: boolean, channel: string}>}
   */
  async validateTemplate(templateId, channel) {
    try {
      const response = await notificationApi.get(`/templates/${templateId}/validate`, {
        params: { channel },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to validate template:', error);
      if (error.response?.status === 404) {
        return { exists: false, isActive: false, channel };
      }
      throw error;
    }
  },

  /**
   * Get all templates for a specific schema
   * @param {string} schemaId - Schema ID
   */
  async getSchemaTemplates(schemaId) {
    try {
      const response = await notificationApi.get(`/templates/schema/${schemaId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get schema templates:', error);
      throw error;
    }
  },

  /**
   * Update templates for a specific schema
   * @param {string} schemaId - Schema ID
   * @param {Object} templates - Template configuration
   */
  async updateSchemaTemplates(schemaId, templates) {
    try {
      const response = await notificationApi.put(`/templates/schema/${schemaId}`, templates);
      return response.data;
    } catch (error) {
      console.error('Failed to update schema templates:', error);
      throw error;
    }
  },

  /**
   * Get missing template warnings
   * @returns {Promise<Object>} Warning result with missing templates
   */
  async getTemplateWarnings() {
    try {
      const response = await notificationApi.get('/templates/warnings');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to get template warnings:', error);
      throw error;
    }
  },

  /**
   * Get template coverage report
   * @returns {Promise<Object>} Coverage report
   */
  async getTemplateCoverage() {
    try {
      const response = await notificationApi.get('/templates/coverage');
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to get template coverage:', error);
      throw error;
    }
  },

  /**
   * Seed templates from exported JSON data
   * @param {Object} seedData - Seed data containing email, SMS, and push templates
   * @returns {Promise<Object>} Seeding result with counts
   */
  async seedTemplates(seedData) {
    try {
      const response = await notificationApi.post('/templates/seed', seedData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to seed templates:', error);
      throw error;
    }
  },

  /**
   * Preview a template with sample/test data
   * @param {string} id - Template ID
   * @param {Object} variables - Optional variables to use for rendering
   * @returns {Promise<{content: string, channel: string}>} Preview result
   */
  async previewTemplate(id, variables) {
    try {
      const response = await notificationApi.post(`/templates/${id}/preview`, { variables });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to preview template:', error);
      throw error;
    }
  },

  // ============= Tenant Template Methods =============

  /**
   * List tenant templates with filters
   * @param {string} tenantId - Tenant ID
   * @param {Object} filters - Filter options
   * @param {string} filters.notificationType - Filter by notification type
   * @param {string} filters.channel - Filter by channel
   * @param {string} filters.language - Filter by language
   * @param {boolean} filters.isActive - Filter by active status
   * @param {number} filters.page - Page number (default: 1)
   * @param {number} filters.limit - Items per page (default: 20)
   * @returns {Promise<{templates: Array, total: number, page: number, limit: number}>}
   */
  async listTenantTemplates(tenantId, filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.notificationType) params.append('notificationType', filters.notificationType);
      if (filters.channel) params.append('channel', filters.channel);
      if (filters.language) params.append('language', filters.language);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const urlPath = params.toString()
        ? `templates/tenant/${tenantId}?${params.toString()}`
        : `templates/tenant/${tenantId}`;

      const response = await notificationApi.get(urlPath);
      const {
        templates,
        total,
        page,
        limit,
      } = response.data || {};

      if (templates && Array.isArray(templates)) {
        return {
          templates,
          total: total || 0,
          page: page || filters.page || 1,
          limit: limit || filters.limit || 20,
        };
      }

      return {
        templates: [],
        total: 0,
        page: filters.page || 1,
        limit: filters.limit || 20,
      };
    } catch (error) {
      console.error('Failed to list tenant templates:', error);
      throw error;
    }
  },

  /**
   * Get a single tenant template by ID
   * @param {string} tenantId - Tenant ID
   * @param {string} id - Template ID
   * @returns {Promise<Object>} Template object
   */
  async getTenantTemplate(tenantId, id) {
    try {
      const response = await notificationApi.get(`templates/tenant/${tenantId}/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to get tenant template:', error);
      if (error.response?.status === 404) {
        throw new Error('Template not found');
      }
      throw error;
    }
  },

  /**
   * Create a new tenant template
   * @param {string} tenantId - Tenant ID
   * @param {Object} templateData - Template data
   * @returns {Promise<Object>} Created template
   */
  async createTenantTemplate(tenantId, templateData) {
    try {
      const response = await notificationApi.post(`templates/tenant/${tenantId}`, templateData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to create tenant template:', error);
      throw error;
    }
  },

  /**
   * Update an existing tenant template
   * @param {string} tenantId - Tenant ID
   * @param {string} id - Template ID
   * @param {Object} templateData - Updated template data
   * @returns {Promise<Object>} Updated template
   */
  async updateTenantTemplate(tenantId, id, templateData) {
    try {
      const response = await notificationApi.put(`templates/tenant/${tenantId}/${id}`, templateData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to update tenant template:', error);
      throw error;
    }
  },

  /**
   * Delete a tenant template
   * @param {string} tenantId - Tenant ID
   * @param {string} id - Template ID
   * @param {boolean} hardDelete - Whether to hard delete (default: false)
   * @returns {Promise<void>}
   */
  async deleteTenantTemplate(tenantId, id, hardDelete = false) {
    try {
      const url = `templates/tenant/${tenantId}/${id}${hardDelete ? '?hard=true' : ''}`;
      await notificationApi.delete(url);
    } catch (error) {
      console.error('Failed to delete tenant template:', error);
      throw error;
    }
  },

  /**
   * Seed tenant templates from JSON data
   * @param {string} tenantId - Tenant ID
   * @param {Object} seedData - Seed data containing email, SMS, push, and popup templates
   * @returns {Promise<Object>} Seeding result with counts
   */
  async seedTenantTemplates(tenantId, seedData) {
    try {
      const response = await notificationApi.post(`templates/tenant/${tenantId}/seed`, seedData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to seed tenant templates:', error);
      throw error;
    }
  },

  /**
   * Preview a tenant template with sample/test data
   * @param {string} tenantId - Tenant ID
   * @param {string} id - Template ID
   * @param {Object} variables - Optional variables to use for rendering
   * @returns {Promise<{content: string, channel: string}>} Preview result
   */
  async previewTenantTemplate(tenantId, id, variables) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required for tenant template preview');
      }
      if (!id) {
        throw new Error('Template ID is required for preview');
      }

      const response = await notificationApi.post(`templates/tenant/${tenantId}/${id}/preview`, { variables });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to preview tenant template:', error);
      throw error;
    }
  },
};
